import { forwardRef, useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import BattleCanvas from "@/components/battlefield/BattleCanvas";

// ---------------------------------------------------------------------------
// Battlefield3D — Phase 7 modern-military rework.
//   Bulls = green army, Bears = red army. Detailed humanoid soldiers with
//   rifles fight across a trench line. Each side has a massive nuclear
//   missile at the rear that launches on top-0.1% trades: arcs across the
//   battlefield, detonates with a mushroom cloud, blast wave, and wipes
//   enemy soldiers within blast radius.
// ---------------------------------------------------------------------------

const COLORS = {
  sky: 0x0b1524,
  fog: 0x1a1f2c,
  grass: 0x445c30,
  grassDark: 0x374a25,
  grassMud: 0x5a4a30,
  dirt: 0x8a6b3f,
  dirtDark: 0x604828,
  dirtBurn: 0x2a1a10,
  water: 0x2d5a8f,
  // Bull (green) army
  bullUniform: 0x3d5c2a,
  bullUniformDark: 0x2a4520,
  bullHelmet: 0x2a3a1a,
  bullBoots: 0x2a2010,
  bullSkin: 0xd0a080,
  bullPack: 0x4a3520,
  bullPennant: 0x2a4520,
  // Bear (red) army
  bearUniform: 0x7a2a2a,
  bearUniformDark: 0x4a1010,
  bearHelmet: 0x151515,
  bearBoots: 0x151515,
  bearSkin: 0xd0a080,
  bearPack: 0x3a1a1a,
  bearPennant: 0x4a1010,
  // Rifle
  rifle: 0x1a1a1a,
  rifleStock: 0x4a3520,
  // Sandbags + trench
  sandbag: 0xb09068,
  sandbagDark: 0x8a6f4a,
  barbedWire: 0x2a2a2a,
  // Nuke
  nukeBullBody: 0x556b3a,
  nukeBullBand: 0xf0e4c0,
  nukeBearBody: 0x6a1e18,
  nukeBearBand: 0x151515,
  nukeNose: 0x8a1e12,
  nukeFin: 0x2a2a2a,
  nukePad: 0x555555,
  warningLight: 0xff1a1a,
  // Bunker / tank / radar
  bunker: 0x555555,
  bunkerDark: 0x333333,
  tankHull: 0x3a4a2a,
  tankHullBear: 0x4a2820,
  radar: 0x555555,
  // Fx
  bullTracer: 0xd8ff88,
  bearTracer: 0xff8848,
  muzzleFlash: 0xfff2b0,
  dust: 0xd6bb8c,
  blood: 0x8a1010,
  explosion: 0xffb040,
  explosionOuter: 0x8a3020,
  mushroomBase: 0xff9040,
  mushroomMid: 0xd06030,
  mushroomTop: 0x6a5a55,
  scorch: 0x181008,
  // Trees / flag
  treeTrunk: 0x4a3520,
  treeFoliage: 0x1e4a2a,
  treeShattered: 0x2a2a20,
  flagBull: 0x3d5c2a,
  flagBear: 0x7a2a2a,
  flagPole: 0x6a6a6a,
  ambient: 0x8090a5,
  sun: 0xffc59f,
};

function setVertexColor(geo, hex) {
  const color = new THREE.Color(hex);
  const count = geo.attributes.position.count;
  const colors = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    colors[i * 3] = color.r;
    colors[i * 3 + 1] = color.g;
    colors[i * 3 + 2] = color.b;
  }
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  return geo;
}

function frontLineWave(z) {
  return Math.sin(z * 0.15) * 2.0 +
         Math.sin(z * 0.31 + 1.7) * 1.2 +
         Math.sin(z * 0.6 + 0.9) * 0.5;
}

function detectWebGLContextType(canvas) {
  try {
    if (canvas.getContext("webgl2")) return "webgl2";
    if (canvas.getContext("webgl")) return "webgl";
    if (canvas.getContext("experimental-webgl")) return "webgl";
  } catch { /* fall through */ }
  return null;
}

// ---- Soldier geometry -----------------------------------------------------
// Slightly rotated so the soldier faces +X by default.
function buildSoldierGeo(species, isOfficer) {
  const isBull = species === "bull";
  const uni = isBull ? COLORS.bullUniform : COLORS.bearUniform;
  const uniDark = isBull ? COLORS.bullUniformDark : COLORS.bearUniformDark;
  const helm = isBull ? COLORS.bullHelmet : COLORS.bearHelmet;
  const boots = isBull ? COLORS.bullBoots : COLORS.bearBoots;
  const skin = isBull ? COLORS.bullSkin : COLORS.bearSkin;
  const pack = isBull ? COLORS.bullPack : COLORS.bearPack;
  const parts = [];

  // Legs (2 cylinders)
  const legL = new THREE.CylinderGeometry(0.09, 0.09, 0.5, 6);
  legL.translate(0, 0.25, 0.09);
  parts.push(setVertexColor(legL, uni));
  const legR = new THREE.CylinderGeometry(0.09, 0.09, 0.5, 6);
  legR.translate(0, 0.25, -0.09);
  parts.push(setVertexColor(legR, uni));
  // Boots
  const bootL = new THREE.BoxGeometry(0.16, 0.08, 0.15);
  bootL.translate(0.02, 0.04, 0.09);
  parts.push(setVertexColor(bootL, boots));
  const bootR = new THREE.BoxGeometry(0.16, 0.08, 0.15);
  bootR.translate(0.02, 0.04, -0.09);
  parts.push(setVertexColor(bootR, boots));
  // Torso
  const torso = new THREE.BoxGeometry(0.24, 0.44, 0.32);
  torso.translate(0, 0.72, 0);
  parts.push(setVertexColor(torso, uni));
  // Chest strap / accent
  const strap = new THREE.BoxGeometry(0.245, 0.06, 0.32);
  strap.translate(0, 0.82, 0);
  parts.push(setVertexColor(strap, uniDark));
  // Backpack
  const pk = new THREE.BoxGeometry(0.14, 0.36, 0.28);
  pk.translate(-0.16, 0.72, 0);
  parts.push(setVertexColor(pk, pack));
  // Head (skin)
  const head = new THREE.BoxGeometry(0.2, 0.18, 0.2);
  head.translate(0, 1.05, 0);
  parts.push(setVertexColor(head, skin));
  // Helmet (dome)
  const helmet = new THREE.SphereGeometry(0.14, 8, 6, 0, Math.PI * 2, 0, Math.PI / 2);
  helmet.translate(0, 1.16, 0);
  parts.push(setVertexColor(helmet, helm));
  // Helmet brim
  const brim = new THREE.CylinderGeometry(0.15, 0.15, 0.03, 8);
  brim.translate(0, 1.16, 0);
  parts.push(setVertexColor(brim, helm));
  // Officer taller helmet cap
  if (isOfficer) {
    const cap = new THREE.CylinderGeometry(0.08, 0.11, 0.1, 6);
    cap.translate(0, 1.32, 0);
    parts.push(setVertexColor(cap, helm));
  }
  // Arms (2 cylinders)
  const armL = new THREE.CylinderGeometry(0.06, 0.06, 0.38, 6);
  armL.rotateX(-0.15);
  armL.translate(0.02, 0.72, 0.2);
  parts.push(setVertexColor(armL, uni));
  const armR = new THREE.CylinderGeometry(0.06, 0.06, 0.38, 6);
  armR.rotateX(0.15);
  armR.translate(0.02, 0.72, -0.2);
  parts.push(setVertexColor(armR, uni));
  // Rifle held forward — cylinder + stock
  const rifleBarrel = new THREE.CylinderGeometry(0.025, 0.025, 0.55, 6);
  rifleBarrel.rotateZ(Math.PI / 2);
  rifleBarrel.translate(0.35, 0.7, 0.05);
  parts.push(setVertexColor(rifleBarrel, COLORS.rifle));
  const rifleStock = new THREE.BoxGeometry(0.16, 0.08, 0.06);
  rifleStock.translate(0.1, 0.7, 0.05);
  parts.push(setVertexColor(rifleStock, COLORS.rifleStock));
  // Officer pennant (small flag on the back)
  if (isOfficer) {
    const polePart = new THREE.CylinderGeometry(0.015, 0.015, 0.5, 4);
    polePart.translate(-0.2, 1.15, 0);
    parts.push(setVertexColor(polePart, COLORS.flagPole));
    const flagCloth = new THREE.BoxGeometry(0.24, 0.14, 0.01);
    flagCloth.translate(-0.32, 1.28, 0);
    parts.push(setVertexColor(flagCloth, isBull ? COLORS.bullPennant : COLORS.bearPennant));
  }
  return mergeGeometries(parts);
}

// ---- Nuke missile (per side, built as a Group to allow easy transform) ----
function buildNukeMissileGroup(species) {
  const isBull = species === "bull";
  const bodyColor = isBull ? COLORS.nukeBullBody : COLORS.nukeBearBody;
  const bandColor = isBull ? COLORS.nukeBullBand : COLORS.nukeBearBand;
  const group = new THREE.Group();

  // Body (segmented cylinders for banding effect)
  const nSeg = 5;
  const bodyH = 4.0;
  const segH = bodyH / nSeg;
  for (let i = 0; i < nSeg; i++) {
    const geo = new THREE.CylinderGeometry(0.4, 0.4, segH * 0.98, 16);
    const col = i % 2 === 0 ? bodyColor : bandColor;
    const mat = new THREE.MeshLambertMaterial({ color: col, flatShading: true });
    const seg = new THREE.Mesh(geo, mat);
    seg.position.y = segH / 2 + i * segH;
    seg.castShadow = true;
    group.add(seg);
  }
  // Nose cone
  const noseGeo = new THREE.ConeGeometry(0.4, 0.9, 16);
  const nose = new THREE.Mesh(noseGeo, new THREE.MeshLambertMaterial({
    color: COLORS.nukeNose,
    flatShading: true,
  }));
  nose.position.y = bodyH + 0.45;
  nose.castShadow = true;
  group.add(nose);
  // Fins (4)
  const finGeo = new THREE.BoxGeometry(0.05, 0.6, 0.55);
  const finMat = new THREE.MeshLambertMaterial({ color: COLORS.nukeFin, flatShading: true });
  for (let i = 0; i < 4; i++) {
    const fin = new THREE.Mesh(finGeo, finMat);
    fin.position.y = 0.3;
    const angle = (i / 4) * Math.PI * 2;
    fin.position.x = Math.cos(angle) * 0.42;
    fin.position.z = Math.sin(angle) * 0.42;
    fin.rotation.y = angle;
    fin.castShadow = true;
    group.add(fin);
  }
  // Warning light — small red emissive sphere at nose
  const warnGeo = new THREE.SphereGeometry(0.09, 8, 6);
  const warnMat = new THREE.MeshBasicMaterial({
    color: COLORS.warningLight, toneMapped: false,
  });
  const warn = new THREE.Mesh(warnGeo, warnMat);
  warn.position.y = bodyH + 0.85;
  warn.name = "warningLight";
  group.add(warn);
  // Text plaque (canvas texture on a plane on the side)
  const c = document.createElement("canvas");
  c.width = 256; c.height = 64;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, 256, 64);
  ctx.strokeStyle = isBull ? "#2a4520" : "#4a1010";
  ctx.lineWidth = 6;
  ctx.strokeRect(3, 3, 250, 58);
  ctx.fillStyle = isBull ? "#2a4520" : "#4a1010";
  ctx.font = "bold 34px 'Unbounded', system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(isBull ? "BULL-01" : "BEAR-01", 128, 34);
  const tex = new THREE.CanvasTexture(c);
  tex.minFilter = THREE.LinearFilter;
  const plaqueMat = new THREE.MeshBasicMaterial({
    map: tex, transparent: true, toneMapped: false,
  });
  const plaque = new THREE.Mesh(new THREE.PlaneGeometry(0.7, 0.18), plaqueMat);
  plaque.position.set(0, 2.2, 0.42);
  group.add(plaque);
  return group;
}

// ---- Nuke launch pad ------------------------------------------------------
function buildNukePadGroup() {
  const group = new THREE.Group();
  const base = new THREE.Mesh(
    new THREE.BoxGeometry(2.4, 0.35, 2.4),
    new THREE.MeshLambertMaterial({ color: COLORS.nukePad, flatShading: true }),
  );
  base.position.y = 0.175;
  base.receiveShadow = true;
  base.castShadow = true;
  group.add(base);
  // 4 vertical support struts around the missile
  for (let i = 0; i < 4; i++) {
    const s = new THREE.Mesh(
      new THREE.BoxGeometry(0.14, 3.4, 0.14),
      new THREE.MeshLambertMaterial({ color: COLORS.bunkerDark, flatShading: true }),
    );
    const angle = (i / 4) * Math.PI * 2 + Math.PI / 4;
    s.position.set(Math.cos(angle) * 0.95, 1.7 + 0.175, Math.sin(angle) * 0.95);
    s.castShadow = true;
    group.add(s);
  }
  return group;
}

// ---- Sandbag stack --------------------------------------------------------
function buildSandbagStackGeo() {
  const parts = [];
  // 3 rows of 4 sandbags, staggered
  for (let row = 0; row < 3; row++) {
    for (let col = 0; col < 4; col++) {
      const bag = new THREE.CapsuleGeometry(0.16, 0.24, 4, 6);
      bag.rotateZ(Math.PI / 2);
      const stagger = row * 0.1;
      bag.translate(
        (col - 1.5) * 0.32 + stagger,
        0.16 + row * 0.28,
        0,
      );
      parts.push(setVertexColor(bag, row % 2 === 0 ? COLORS.sandbag : COLORS.sandbagDark));
    }
  }
  return mergeGeometries(parts);
}

function buildBunkerGeo() {
  const parts = [];
  const base = new THREE.BoxGeometry(2.6, 1.2, 1.8);
  base.translate(0, 0.6, 0);
  parts.push(setVertexColor(base, COLORS.bunker));
  // Rounded top (half cylinder)
  const top = new THREE.CylinderGeometry(0.9, 0.9, 2.6, 12, 1, false, 0, Math.PI);
  top.rotateZ(Math.PI / 2);
  top.rotateY(Math.PI / 2);
  top.translate(0, 1.2, 0);
  parts.push(setVertexColor(top, COLORS.bunkerDark));
  // Slit window (dark box embedded on front)
  const slit = new THREE.BoxGeometry(1.4, 0.16, 0.05);
  slit.translate(0, 0.9, 0.92);
  parts.push(setVertexColor(slit, 0x080808));
  return mergeGeometries(parts);
}

function buildTankGeo(species) {
  const isBull = species === "bull";
  const hullCol = isBull ? COLORS.tankHull : COLORS.tankHullBear;
  const parts = [];
  const hull = new THREE.BoxGeometry(2.4, 0.7, 1.4);
  hull.translate(0, 0.55, 0);
  parts.push(setVertexColor(hull, hullCol));
  // Tracks
  const trackL = new THREE.BoxGeometry(2.5, 0.35, 0.3);
  trackL.translate(0, 0.2, 0.65);
  parts.push(setVertexColor(trackL, 0x1a1a1a));
  const trackR = new THREE.BoxGeometry(2.5, 0.35, 0.3);
  trackR.translate(0, 0.2, -0.65);
  parts.push(setVertexColor(trackR, 0x1a1a1a));
  // Turret
  const turret = new THREE.CylinderGeometry(0.6, 0.7, 0.5, 12);
  turret.translate(-0.1, 1.15, 0);
  parts.push(setVertexColor(turret, hullCol));
  // Barrel
  const barrel = new THREE.CylinderGeometry(0.08, 0.08, 1.6, 8);
  barrel.rotateZ(Math.PI / 2);
  barrel.translate(0.85, 1.15, 0);
  parts.push(setVertexColor(barrel, 0x1a1a1a));
  return mergeGeometries(parts);
}

function buildRadarGeo() {
  const parts = [];
  const legs = new THREE.CylinderGeometry(0.05, 0.05, 3.2, 6);
  legs.translate(0, 1.6, 0);
  parts.push(setVertexColor(legs, COLORS.radar));
  const cross = new THREE.BoxGeometry(0.6, 0.06, 0.06);
  cross.translate(0, 2.8, 0);
  parts.push(setVertexColor(cross, COLORS.radar));
  const dish = new THREE.CylinderGeometry(0.5, 0.1, 0.06, 12);
  dish.rotateX(Math.PI / 3);
  dish.translate(0, 3.2, 0);
  parts.push(setVertexColor(dish, 0xffffff));
  return mergeGeometries(parts);
}

function buildTreeGeo(shattered = false) {
  const parts = [];
  const trunk = new THREE.CylinderGeometry(0.12, 0.16, shattered ? 0.4 : 0.7, 6);
  trunk.translate(0, shattered ? 0.2 : 0.35, 0);
  parts.push(setVertexColor(trunk, shattered ? COLORS.treeShattered : COLORS.treeTrunk));
  if (!shattered) {
    const f1 = new THREE.ConeGeometry(0.65, 1.5, 6);
    f1.translate(0, 1.15, 0);
    parts.push(setVertexColor(f1, COLORS.treeFoliage));
    const f2 = new THREE.ConeGeometry(0.42, 1.0, 6);
    f2.translate(0, 1.7, 0);
    parts.push(setVertexColor(f2, COLORS.treeFoliage));
  }
  return mergeGeometries(parts);
}

// Command flag with painted BULL / BEAR text
function buildCommandFlag(species) {
  const isBull = species === "bull";
  const group = new THREE.Group();
  const pole = new THREE.Mesh(
    new THREE.CylinderGeometry(0.06, 0.06, 5.5, 8),
    new THREE.MeshLambertMaterial({ color: COLORS.flagPole, flatShading: true }),
  );
  pole.position.y = 2.75;
  pole.castShadow = true;
  group.add(pole);
  // Flag cloth with text
  const c = document.createElement("canvas");
  c.width = 256; c.height = 160;
  const ctx = c.getContext("2d");
  ctx.fillStyle = isBull ? "#3d5c2a" : "#7a2a2a";
  ctx.fillRect(0, 0, 256, 160);
  // Emblem circle
  ctx.fillStyle = "rgba(255,255,255,0.15)";
  ctx.beginPath();
  ctx.arc(128, 60, 40, 0, Math.PI * 2);
  ctx.fill();
  // Bull / Bear text
  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 42px 'Unbounded', system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(isBull ? "BULLS" : "BEARS", 128, 60);
  ctx.font = "bold 22px 'Unbounded', system-ui, sans-serif";
  ctx.fillText("HQ", 128, 120);
  const tex = new THREE.CanvasTexture(c);
  tex.minFilter = THREE.LinearFilter;
  const flag = new THREE.Mesh(
    new THREE.PlaneGeometry(2.2, 1.4),
    new THREE.MeshBasicMaterial({ map: tex, side: THREE.DoubleSide, toneMapped: false }),
  );
  flag.position.set(1.1, 4.5, 0);
  flag.castShadow = true;
  group.add(flag);
  return group;
}

// ---------------------------------------------------------------------------

const Battlefield3D = forwardRef(function Battlefield3D(
  {
    bookRef,
    hitsRef,
    mobile = false,
    heightClass = "h-[70vh]",
    topClass = "top-[18vh]",
    autoOrbit = false,
    hq = true,
    onKill,
    compact = false,
    debug = false,
    nukeStateRef,
  },
  ref,
) {
  const localRef = useRef(null);
  const canvasRef = ref || localRef;
  const [contextStatus, setContextStatus] = useState(null);
  const autoOrbitRef = useRef(autoOrbit);
  const hqRef = useRef(hq);
  const onKillRef = useRef(onKill);
  useEffect(() => { autoOrbitRef.current = autoOrbit; }, [autoOrbit]);
  useEffect(() => { hqRef.current = hq && !mobile && !compact; }, [hq, mobile, compact]);
  useEffect(() => { onKillRef.current = onKill; }, [onKill]);

  useEffect(() => {
    if (contextStatus === "fallback-2d") return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    // ---- constants ---------------------------------------------------------
    const PLANE_W = compact ? 40 : 60;
    const PLANE_H = compact ? 22 : 30;
    const MAX_BULLS = compact ? 18 : mobile ? 32 : 70;
    const MAX_BEARS = compact ? 18 : mobile ? 32 : 70;
    const OFFICER_RATIO = 0.08;
    const MAX_TRACERS = 60;
    const MAX_FLASHES = 40;
    const MAX_DUST = 240;
    const T_MIN_X = -PLANE_W / 2 + 2;
    const T_MAX_X = PLANE_W / 2 - 2;
    const T_MIN_Z = -PLANE_H / 2 + 1.5;
    const T_MAX_Z = PLANE_H / 2 - 1.5;
    const NUKE_BULL_X = compact ? -10 : -13;
    const NUKE_BEAR_X = compact ? 10 : 13;
    const NUKE_COOLDOWN = 45;
    const BLAST_RADIUS = 10;

    // ---- scene / camera / lights -----------------------------------------
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(COLORS.sky);
    scene.fog = new THREE.Fog(COLORS.fog, PLANE_W * 0.55, PLANE_W * 1.35);

    const aspect = (canvas.clientWidth || 1) / (canvas.clientHeight || 1);
    const camera = new THREE.PerspectiveCamera(compact ? 48 : 44, aspect, 0.1, 250);
    const camY = compact ? 18 : mobile ? 22 : 20;
    const camDistBase = compact ? 30 : mobile ? 36 : 34;
    camera.position.set(0, camY, camDistBase);
    camera.lookAt(0, 0, 0);

    const dirLight = new THREE.DirectionalLight(COLORS.sun, 0.95);
    dirLight.position.set(24, 32, 14);
    if (!mobile && !compact) {
      dirLight.castShadow = true;
      dirLight.shadow.mapSize.width = 1024;
      dirLight.shadow.mapSize.height = 1024;
      dirLight.shadow.camera.left = -PLANE_W / 2;
      dirLight.shadow.camera.right = PLANE_W / 2;
      dirLight.shadow.camera.top = PLANE_H / 2;
      dirLight.shadow.camera.bottom = -PLANE_H / 2;
      dirLight.shadow.camera.near = 1;
      dirLight.shadow.camera.far = 80;
      dirLight.shadow.bias = -0.001;
    }
    scene.add(dirLight);
    scene.add(new THREE.AmbientLight(COLORS.ambient, 0.55));
    scene.add(new THREE.HemisphereLight(0xaac0ff, 0x604020, 0.25));

    // ---- terrain (grungy split with crater patches via noise) -----------
    const terrainGeo = new THREE.PlaneGeometry(PLANE_W, PLANE_H, 1, 1);
    terrainGeo.rotateX(-Math.PI / 2);
    const terrainMat = new THREE.ShaderMaterial({
      uniforms: {
        uFrontLineX: { value: 0 },
        uGrass: { value: new THREE.Color(COLORS.grass) },
        uGrassDark: { value: new THREE.Color(COLORS.grassDark) },
        uGrassMud: { value: new THREE.Color(COLORS.grassMud) },
        uDirt: { value: new THREE.Color(COLORS.dirt) },
        uDirtDark: { value: new THREE.Color(COLORS.dirtDark) },
        uDirtBurn: { value: new THREE.Color(COLORS.dirtBurn) },
        uPlaneSize: { value: new THREE.Vector2(PLANE_W, PLANE_H) },
        uScorchX: { value: 0 },
        uScorchZ: { value: 0 },
        uScorchAge: { value: 999 }, // > SCORCH_LIFETIME initially = no scorch
      },
      vertexShader: `
        varying vec2 vUv;
        varying vec3 vWorldPos;
        void main() {
          vUv = uv;
          vWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float uFrontLineX;
        uniform vec3 uGrass;
        uniform vec3 uGrassDark;
        uniform vec3 uGrassMud;
        uniform vec3 uDirt;
        uniform vec3 uDirtDark;
        uniform vec3 uDirtBurn;
        uniform vec2 uPlaneSize;
        uniform float uScorchX;
        uniform float uScorchZ;
        uniform float uScorchAge;
        varying vec2 vUv;
        varying vec3 vWorldPos;
        float wave(float z) {
          return sin(z * 0.15) * 2.0 + sin(z * 0.31 + 1.7) * 1.2 + sin(z * 0.6 + 0.9) * 0.5;
        }
        float rand(vec2 p) {
          return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
        }
        // Cheap 2D noise
        float noise2(vec2 p) {
          vec2 i = floor(p);
          vec2 f = fract(p);
          float a = rand(i);
          float b = rand(i + vec2(1.0, 0.0));
          float c = rand(i + vec2(0.0, 1.0));
          float d = rand(i + vec2(1.0, 1.0));
          vec2 u = f * f * (3.0 - 2.0 * f);
          return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
        }
        void main() {
          float worldX = vWorldPos.x;
          float worldZ = vWorldPos.z;
          float front = uFrontLineX + wave(worldZ);
          float mixT = smoothstep(front - 0.4, front + 0.4, worldX);
          // Grass side
          float g1 = rand(floor(vUv * 240.0));
          float mudPatch = smoothstep(0.55, 0.75, noise2(vUv * 6.0 + 3.0));
          vec3 grassBase = mix(uGrass, uGrassDark, step(0.7, rand(floor(vUv * 90.0 + 13.0))));
          vec3 grass = mix(grassBase * (0.9 + g1 * 0.14), uGrassMud, mudPatch * 0.5);
          // Small craters on grass side
          float craterMask = smoothstep(0.6, 0.85, noise2(vUv * 14.0));
          grass = mix(grass, grass * 0.6, craterMask * 0.4);
          // Dirt side
          float d1 = rand(floor(vUv * 200.0 + 41.0));
          vec3 dirtBase = mix(uDirt, uDirtDark, step(0.55, rand(floor(vUv * 80.0 + 7.0))));
          vec3 dirt = dirtBase * (0.86 + d1 * 0.2);
          float burnPatch = smoothstep(0.6, 0.8, noise2(vUv * 8.0 + 7.0));
          dirt = mix(dirt, uDirtBurn, burnPatch * 0.5);
          // Larger craters on dirt side
          float dirtCrater = smoothstep(0.55, 0.85, noise2(vUv * 10.0 + 11.0));
          dirt = mix(dirt, dirt * 0.5, dirtCrater * 0.5);
          vec3 col = mix(grass, dirt, mixT);
          // Nuke scorch overlay (persistent for a few seconds)
          if (uScorchAge < 15.0) {
            float d = distance(vec2(worldX, worldZ), vec2(uScorchX, uScorchZ));
            float scorchMask = smoothstep(6.0, 0.5, d) * (1.0 - uScorchAge / 15.0);
            col = mix(col, uDirtBurn * 0.4, scorchMask);
          }
          gl_FragColor = vec4(col, 1.0);
        }
      `,
    });
    const ground = new THREE.Mesh(terrainGeo, terrainMat);
    ground.receiveShadow = true;
    scene.add(ground);

    // Optional water/crater pond
    const pondGeo = new THREE.PlaneGeometry(4, 3);
    pondGeo.rotateX(-Math.PI / 2);
    pondGeo.translate(-PLANE_W * 0.35, 0.02, -PLANE_H * 0.35);
    scene.add(new THREE.Mesh(pondGeo, new THREE.MeshLambertMaterial({
      color: COLORS.water, flatShading: true,
    })));

    // ---- trench sandbags along both sides of the front line ------------
    const sandbagGeo = buildSandbagStackGeo();
    const sandbagMat = new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true });
    const N_TRENCH = compact ? 12 : 24;
    const sandbagMesh = new THREE.InstancedMesh(sandbagGeo, sandbagMat, N_TRENCH * 2);
    sandbagMesh.castShadow = true;
    sandbagMesh.receiveShadow = true;
    const dummy = new THREE.Object3D();
    for (let i = 0; i < N_TRENCH; i++) {
      const z = -PLANE_H / 2 + 1 + (PLANE_H - 2) * (i / (N_TRENCH - 1));
      const frontX = frontLineWave(z);
      // Bull side (left of trench)
      dummy.position.set(frontX - 0.9, 0, z);
      dummy.rotation.set(0, 0, 0);
      dummy.scale.setScalar(1);
      dummy.updateMatrix();
      sandbagMesh.setMatrixAt(i, dummy.matrix);
      // Bear side (right of trench)
      dummy.position.set(frontX + 0.9, 0, z);
      dummy.rotation.set(0, Math.PI, 0);
      dummy.updateMatrix();
      sandbagMesh.setMatrixAt(N_TRENCH + i, dummy.matrix);
    }
    sandbagMesh.instanceMatrix.needsUpdate = true;
    scene.add(sandbagMesh);

    // Barbed wire (thin dark cylinders) — one continuous set of segments
    // along the trench center
    const wirePoints = [];
    for (let i = 0; i <= 40; i++) {
      const z = -PLANE_H / 2 + (PLANE_H * i) / 40;
      wirePoints.push(new THREE.Vector3(frontLineWave(z), 0.35, z));
    }
    const wireCurve = new THREE.CatmullRomCurve3(wirePoints);
    const wireGeo = new THREE.TubeGeometry(wireCurve, 40, 0.025, 4, false);
    const wireMat = new THREE.MeshLambertMaterial({ color: COLORS.barbedWire, flatShading: true });
    const wire = new THREE.Mesh(wireGeo, wireMat);
    wire.castShadow = false;
    scene.add(wire);

    // Subtle warm haze under the trench (emissive tube)
    let hazeGeo = new THREE.TubeGeometry(wireCurve, 40, 0.35, 6, false);
    const hazeMat = new THREE.MeshBasicMaterial({
      color: 0x8a5a30, toneMapped: false, transparent: true, opacity: 0.18,
      depthWrite: false, blending: THREE.AdditiveBlending,
    });
    const haze = new THREE.Mesh(hazeGeo, hazeMat);
    scene.add(haze);

    // ---- trees ----------------------------------------------------------
    const treeGeo = buildTreeGeo(false);
    const shatteredGeo = buildTreeGeo(true);
    const treeMat = new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true });
    const N_TREES = compact ? 18 : 42;
    const treeMesh = new THREE.InstancedMesh(treeGeo, treeMat, N_TREES);
    const N_SHATTERED = compact ? 6 : 14;
    const shatteredMesh = new THREE.InstancedMesh(shatteredGeo, treeMat, N_SHATTERED);
    treeMesh.castShadow = true;
    shatteredMesh.castShadow = true;
    let placed = 0;
    for (let i = 0; i < N_TREES; i++) {
      let x = 0, z = 0;
      for (let tries = 0; tries < 10; tries++) {
        x = (Math.random() - 0.5) * (PLANE_W - 4);
        z = (Math.random() - 0.5) * (PLANE_H - 4);
        const dFront = Math.abs(x - frontLineWave(z));
        if (dFront > 4.5) break;
      }
      dummy.position.set(x, 0, z);
      dummy.rotation.y = Math.random() * Math.PI * 2;
      dummy.scale.setScalar(0.7 + Math.random() * 0.4);
      dummy.updateMatrix();
      treeMesh.setMatrixAt(placed++, dummy.matrix);
    }
    treeMesh.count = placed;
    treeMesh.instanceMatrix.needsUpdate = true;
    scene.add(treeMesh);
    for (let i = 0; i < N_SHATTERED; i++) {
      let x = 0, z = 0;
      for (let tries = 0; tries < 6; tries++) {
        x = (Math.random() - 0.5) * (PLANE_W - 4);
        z = (Math.random() - 0.5) * (PLANE_H - 4);
        const dFront = Math.abs(x - frontLineWave(z));
        if (dFront > 2 && dFront < 6) break;
      }
      dummy.position.set(x, 0, z);
      dummy.rotation.y = Math.random() * Math.PI * 2;
      dummy.scale.setScalar(0.6 + Math.random() * 0.3);
      dummy.updateMatrix();
      shatteredMesh.setMatrixAt(i, dummy.matrix);
    }
    shatteredMesh.instanceMatrix.needsUpdate = true;
    scene.add(shatteredMesh);

    // ---- bunkers (2 per side) + tanks + radar --------------------------
    const bunkerGeo = buildBunkerGeo();
    const bunkerMat = new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true });
    const bunkers = new THREE.InstancedMesh(bunkerGeo, bunkerMat, 4);
    bunkers.castShadow = true;
    bunkers.receiveShadow = true;
    const bunkerPos = [
      [-PLANE_W / 2 + 8, -4], [-PLANE_W / 2 + 8, 4],
      [ PLANE_W / 2 - 8, -4], [ PLANE_W / 2 - 8, 4],
    ];
    bunkerPos.forEach((p, i) => {
      dummy.position.set(p[0], 0, p[1]);
      dummy.rotation.y = i < 2 ? 0 : Math.PI;
      dummy.scale.setScalar(1);
      dummy.updateMatrix();
      bunkers.setMatrixAt(i, dummy.matrix);
    });
    bunkers.instanceMatrix.needsUpdate = true;
    scene.add(bunkers);

    // Tank per side (single Mesh — few enough not to instance)
    const bullTank = new THREE.Mesh(
      buildTankGeo("bull"),
      new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true }),
    );
    bullTank.position.set(-PLANE_W / 2 + 12, 0, PLANE_H / 2 - 4);
    bullTank.rotation.y = -0.3;
    bullTank.castShadow = true;
    scene.add(bullTank);
    const bearTank = new THREE.Mesh(
      buildTankGeo("bear"),
      new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true }),
    );
    bearTank.position.set(PLANE_W / 2 - 12, 0, PLANE_H / 2 - 4);
    bearTank.rotation.y = Math.PI + 0.3;
    bearTank.castShadow = true;
    scene.add(bearTank);

    // Radar towers
    const radarMat = new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true });
    const bullRadar = new THREE.Mesh(buildRadarGeo(), radarMat);
    bullRadar.position.set(-PLANE_W / 2 + 6, 0, -PLANE_H / 2 + 5);
    bullRadar.castShadow = true;
    scene.add(bullRadar);
    const bearRadar = new THREE.Mesh(buildRadarGeo(), radarMat);
    bearRadar.position.set(PLANE_W / 2 - 6, 0, -PLANE_H / 2 + 5);
    bearRadar.castShadow = true;
    scene.add(bearRadar);

    // Command flags on rear of each side
    const bullFlag = buildCommandFlag("bull");
    bullFlag.position.set(-PLANE_W / 2 + 3, 0, PLANE_H / 2 - 2);
    scene.add(bullFlag);
    const bearFlag = buildCommandFlag("bear");
    bearFlag.position.set(PLANE_W / 2 - 3, 0, PLANE_H / 2 - 2);
    scene.add(bearFlag);

    // ---- nuke silos (missile + pad) -----------------------------------
    const bullPad = buildNukePadGroup();
    bullPad.position.set(NUKE_BULL_X, 0, PLANE_H / 2 - 3.5);
    scene.add(bullPad);
    const bullNuke = buildNukeMissileGroup("bull");
    bullNuke.position.set(NUKE_BULL_X, 0.35, PLANE_H / 2 - 3.5);
    scene.add(bullNuke);
    const bearPad = buildNukePadGroup();
    bearPad.position.set(NUKE_BEAR_X, 0, PLANE_H / 2 - 3.5);
    scene.add(bearPad);
    const bearNuke = buildNukeMissileGroup("bear");
    bearNuke.position.set(NUKE_BEAR_X, 0.35, PLANE_H / 2 - 3.5);
    scene.add(bearNuke);

    // ---- soldier InstancedMeshes (4: bull grunt, bear grunt, bull off, bear off)
    const bullGruntGeo = buildSoldierGeo("bull", false);
    const bullOfficerGeo = buildSoldierGeo("bull", true);
    const bearGruntGeo = buildSoldierGeo("bear", false);
    const bearOfficerGeo = buildSoldierGeo("bear", true);
    const soldierMat = new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true });

    const bullGruntMesh = new THREE.InstancedMesh(bullGruntGeo, soldierMat, MAX_BULLS);
    bullGruntMesh.castShadow = true;
    bullGruntMesh.count = 0;
    bullGruntMesh.frustumCulled = false;
    scene.add(bullGruntMesh);
    const bullOfficerMesh = new THREE.InstancedMesh(bullOfficerGeo, soldierMat, MAX_BULLS);
    bullOfficerMesh.castShadow = true;
    bullOfficerMesh.count = 0;
    bullOfficerMesh.frustumCulled = false;
    scene.add(bullOfficerMesh);
    const bearGruntMesh = new THREE.InstancedMesh(bearGruntGeo, soldierMat, MAX_BEARS);
    bearGruntMesh.castShadow = true;
    bearGruntMesh.count = 0;
    bearGruntMesh.frustumCulled = false;
    scene.add(bearGruntMesh);
    const bearOfficerMesh = new THREE.InstancedMesh(bearOfficerGeo, soldierMat, MAX_BEARS);
    bearOfficerMesh.castShadow = true;
    bearOfficerMesh.count = 0;
    bearOfficerMesh.frustumCulled = false;
    scene.add(bearOfficerMesh);

    // ---- soldier AI state ---------------------------------------------
    const bulls = [];
    const bears = [];
    const initSoldier = (species) => {
      const isBull = species === "bull";
      const backX = isBull
        ? T_MIN_X + Math.random() * (T_MAX_X - T_MIN_X) * 0.55
        : T_MAX_X - Math.random() * (T_MAX_X - T_MIN_X) * 0.55;
      return {
        species,
        isOfficer: Math.random() < OFFICER_RATIO,
        x: backX,
        z: T_MIN_Z + Math.random() * (T_MAX_Z - T_MIN_Z),
        yaw: isBull ? 0 : Math.PI,
        role: Math.random() < 0.7 ? "advancer" : "support",
        wobbleSeed: Math.random() * Math.PI * 2,
        wanderDir: Math.random() * Math.PI * 2,
        wanderChangeAt: 0,
        deathAt: 0,
        respawnAt: 0,
        alive: true,
        dustSpawned: false,
        nextFireAt: 0.5 + Math.random() * 3,
      };
    };
    for (let i = 0; i < MAX_BULLS; i++) bulls.push(initSoldier("bull"));
    for (let i = 0; i < MAX_BEARS; i++) bears.push(initSoldier("bear"));

    // ---- tracer + muzzle-flash + dust pools --------------------------
    const tracerGeo = new THREE.CylinderGeometry(0.05, 0.05, 0.4, 6);
    const bullTracerMat = new THREE.MeshBasicMaterial({
      color: COLORS.bullTracer, toneMapped: false,
      transparent: true, opacity: 0.95,
      blending: THREE.AdditiveBlending, depthWrite: false,
    });
    const bearTracerMat = new THREE.MeshBasicMaterial({
      color: COLORS.bearTracer, toneMapped: false,
      transparent: true, opacity: 0.95,
      blending: THREE.AdditiveBlending, depthWrite: false,
    });
    const bullTracerMesh = new THREE.InstancedMesh(tracerGeo, bullTracerMat, MAX_TRACERS);
    bullTracerMesh.count = 0; bullTracerMesh.frustumCulled = false;
    scene.add(bullTracerMesh);
    const bearTracerMesh = new THREE.InstancedMesh(tracerGeo, bearTracerMat, MAX_TRACERS);
    bearTracerMesh.count = 0; bearTracerMesh.frustumCulled = false;
    scene.add(bearTracerMesh);

    const flashGeo = new THREE.SphereGeometry(0.3, 6, 4);
    const flashMat = new THREE.MeshBasicMaterial({
      color: COLORS.muzzleFlash, toneMapped: false,
      transparent: true, opacity: 1,
      blending: THREE.AdditiveBlending, depthWrite: false,
    });
    const flashMesh = new THREE.InstancedMesh(flashGeo, flashMat, MAX_FLASHES);
    flashMesh.count = 0; flashMesh.frustumCulled = false;
    scene.add(flashMesh);

    const dustGeo = new THREE.SphereGeometry(0.16, 6, 4);
    const dustMat = new THREE.MeshBasicMaterial({
      color: COLORS.dust, transparent: true, opacity: 0.55, depthWrite: false,
    });
    const dustMesh = new THREE.InstancedMesh(dustGeo, dustMat, MAX_DUST);
    dustMesh.count = 0; dustMesh.frustumCulled = false;
    scene.add(dustMesh);

    // Blood/dust particle arrays
    const tracers = []; // { isBullShot, fromX,fromY,fromZ, toX,toY,toZ, spawnedAt, kill:bool }
    const flashes = []; // { x,y,z, spawnedAt, duration, size }
    const dust = [];    // { x,y,z, driftX,driftY,driftZ, spawnedAt, color(optional) }

    // ---- nuke state ---------------------------------------------------
    const nukeState = {
      bull: { phase: "idle", cooldownUntil: 0, launchStartedAt: 0, impactAt: 0, targetZ: 0 },
      bear: { phase: "idle", cooldownUntil: 0, launchStartedAt: 0, impactAt: 0, targetZ: 0 },
    };
    // Report to parent via nukeStateRef (poll-friendly)
    const publishNukeState = () => {
      if (!nukeStateRef) return;
      const now = elapsed;
      nukeStateRef.current = {
        bullReady: nukeState.bull.phase === "idle" && now >= nukeState.bull.cooldownUntil,
        bullCooldown: Math.max(0, nukeState.bull.cooldownUntil - now),
        bullPhase: nukeState.bull.phase,
        bearReady: nukeState.bear.phase === "idle" && now >= nukeState.bear.cooldownUntil,
        bearCooldown: Math.max(0, nukeState.bear.cooldownUntil - now),
        bearPhase: nukeState.bear.phase,
      };
    };

    // ---- mushroom cloud state ------------------------------------------
    const mushroomStacks = []; // { x, z, startedAt, layers: [{y, r, color}] }
    const blastRings = [];     // { x, z, startedAt }
    const impactFlash = { active: false, startedAt: 0, x: 0, z: 0 };
    const scorchState = { x: 0, z: 0, age: 999 };
    // Reusable mushroom-layer geometry
    const layerGeo = new THREE.SphereGeometry(1.0, 16, 12);
    // Reusable blast ring geometry
    const ringGeo = new THREE.RingGeometry(0.9, 1.0, 32);
    ringGeo.rotateX(-Math.PI / 2);
    const ringMat = new THREE.MeshBasicMaterial({
      color: 0xffb060, toneMapped: false,
      transparent: true, opacity: 0.8,
      blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide,
    });
    // Impact flash sphere
    const flashSphereGeo = new THREE.SphereGeometry(6, 16, 12);
    const flashSphereMat = new THREE.MeshBasicMaterial({
      color: 0xffffff, toneMapped: false,
      transparent: true, opacity: 0,
      blending: THREE.AdditiveBlending, depthWrite: false,
    });
    const flashSphere = new THREE.Mesh(flashSphereGeo, flashSphereMat);
    flashSphere.visible = false;
    scene.add(flashSphere);

    // ---- renderer + post-process --------------------------------------
    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({
        canvas,
        antialias: !mobile,
        alpha: false,
        preserveDrawingBuffer: false,
      });
    } catch (err) {
      console.warn("Battlefield3D: WebGLRenderer init failed", err);
      setContextStatus("fallback-2d");
      return undefined;
    }
    const detected = detectWebGLContextType(canvas) || "webgl";
    setContextStatus(detected);
    console.info(`Battlefield3D: WebGL context created (${detected})`);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(canvas.clientWidth || 1, canvas.clientHeight || 1, false);
    renderer.setClearColor(COLORS.sky, 1);
    if (!mobile && !compact) {
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    }

    // Composer (bloom + vignette). Instantiated once — always used when hqRef.current.
    let composer = null;
    const VignetteShader = {
      uniforms: {
        tDiffuse: { value: null },
        offset: { value: 1.05 },
        darkness: { value: 1.15 },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D tDiffuse;
        uniform float offset;
        uniform float darkness;
        varying vec2 vUv;
        void main() {
          vec4 col = texture2D(tDiffuse, vUv);
          vec2 uv = (vUv - 0.5) * vec2(offset);
          float vig = 1.0 - dot(uv, uv) * darkness;
          gl_FragColor = vec4(col.rgb * clamp(vig, 0.0, 1.0), col.a);
        }
      `,
    };
    const initComposer = () => {
      if (composer) return composer;
      const c = new EffectComposer(renderer);
      c.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      c.setSize(canvas.clientWidth || 1, canvas.clientHeight || 1);
      c.addPass(new RenderPass(scene, camera));
      const bloom = new UnrealBloomPass(
        new THREE.Vector2(canvas.clientWidth || 1, canvas.clientHeight || 1),
        0.7, 0.4, 0.55,
      );
      c.addPass(bloom);
      c.addPass(new ShaderPass(VignetteShader));
      c.addPass(new OutputPass());
      composer = c;
      return composer;
    };

    // ---- state --------------------------------------------------------
    let elapsed = 0;
    let currentFrontLineX = 0;
    let targetFrontLineX = 0;
    const shake = { until: 0, magnitude: 0 };
    const clock = new THREE.Clock();

    // ---- resize -------------------------------------------------------
    let resizePending = true;
    const resize = () => { resizePending = true; };
    window.addEventListener("resize", resize);
    const applyResize = () => {
      const w = canvas.clientWidth || 1;
      const h = canvas.clientHeight || 1;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h, false);
      if (composer) composer.setSize(w, h);
    };
    applyResize();

    // ---- book pressure ------------------------------------------------
    const updateFrontLineTarget = () => {
      const book = bookRef?.current;
      if (!book) return;
      const bids = book.bids || [];
      const asks = book.asks || [];
      let sumBidUsd = 0, sumAskUsd = 0;
      for (const l of bids) sumBidUsd += l.price * l.qty;
      for (const l of asks) sumAskUsd += l.price * l.qty;
      const total = sumBidUsd + sumAskUsd;
      if (total <= 0) { targetFrontLineX = 0; return; }
      const pressure = (sumBidUsd - sumAskUsd) / total;
      targetFrontLineX = pressure * 6;
    };
    let lastFrontUpdate = 0;

    // ---- combat helpers -----------------------------------------------
    const findFrontLineTarget = (arr) => {
      let best = -1;
      let bestDist = Infinity;
      for (let i = 0; i < arr.length; i++) {
        const s = arr[i];
        if (!s.alive || s.deathAt) continue;
        const front = currentFrontLineX + frontLineWave(s.z);
        const d = Math.abs(s.x - front);
        if (d < bestDist) { bestDist = d; best = i; }
      }
      return best;
    };

    const spawnTracer = (isBull, fromX, fromY, fromZ, toX, toY, toZ, kill) => {
      if (tracers.length >= MAX_TRACERS) return;
      tracers.push({
        isBullShot: isBull,
        fromX, fromY, fromZ,
        toX, toY, toZ,
        spawnedAt: elapsed, duration: 0.18,
        kill: !!kill,
      });
      // Muzzle flash at source
      if (flashes.length < MAX_FLASHES * 2) {
        flashes.push({ x: fromX, y: fromY, z: fromZ, spawnedAt: elapsed, duration: 0.08, size: 0.9 });
      }
    };

    const spawnDust = (x, z, colorHex = COLORS.dust, count = 8) => {
      for (let k = 0; k < count && dust.length < MAX_DUST; k++) {
        const angle = Math.random() * Math.PI * 2;
        const r = 0.1 + Math.random() * 0.4;
        dust.push({
          x, y: 0.1 + Math.random() * 0.25, z,
          driftX: Math.cos(angle) * r,
          driftY: 0.3 + Math.random() * 0.4,
          driftZ: Math.sin(angle) * r,
          spawnedAt: elapsed,
          colorHex,
        });
      }
    };

    // Kill a soldier at a random front-line position on the loser side.
    const killOne = (loserSpecies) => {
      const arr = loserSpecies === "bull" ? bulls : bears;
      const idx = findFrontLineTarget(arr);
      if (idx < 0) return;
      const s = arr[idx];
      s.deathAt = elapsed;
      // Fire a tracer at them from the enemy side
      const shooterX = s.x + (loserSpecies === "bull" ? 1.5 : -1.5);
      spawnTracer(
        loserSpecies === "bear", // bull-tracer if bear died (bull shot)
        shooterX, 0.8, s.z + (Math.random() - 0.5) * 0.6,
        s.x, 0.9, s.z,
        true,
      );
      if (onKillRef.current) {
        try { onKillRef.current(loserSpecies); } catch { /* swallow */ }
      }
    };

    // Kill all soldiers within radius of an impact point.
    const killByBlast = (x, z, radius, loserSpecies) => {
      const arr = loserSpecies === "bull" ? bulls : bears;
      let count = 0;
      for (const s of arr) {
        if (!s.alive || s.deathAt) continue;
        const dx = s.x - x, dz = s.z - z;
        if (dx * dx + dz * dz <= radius * radius) {
          s.deathAt = elapsed + Math.random() * 0.05;
          count++;
        }
      }
      return count;
    };

    // ---- nuke launch --------------------------------------------------
    const launchNuke = (side) => {
      const state = nukeState[side];
      if (state.phase !== "idle" || elapsed < state.cooldownUntil) return false;
      state.phase = "launching";
      state.launchStartedAt = elapsed;
      state.targetZ = (Math.random() - 0.5) * (PLANE_H - 6);
      state.impactAt = elapsed + 2.2;
      // Sky brightening
      skyFlashUntil = elapsed + 3.0;
      return true;
    };
    let skyFlashUntil = 0;

    // ---- trade → combat ------------------------------------------------
    const drainHits = () => {
      const arr = hitsRef?.current;
      if (!arr || arr.length === 0) return;
      while (arr.length > 0) {
        const evt = arr.shift();
        const sev = evt.severity | 0;
        const loser = evt.side === "bid" ? "bull" : "bear";
        const winner = loser === "bull" ? "bear" : "bull";
        // Volley size
        const shots =
          sev >= 2 ? 8 + Math.floor(Math.random() * 5) :
          sev >= 1 ? 3 + Math.floor(Math.random() * 3) : 1;
        for (let i = 0; i < shots; i++) killOne(loser);
        if (sev >= 2) {
          // Extreme → screen shake + attempt nuke launch from winner side
          shake.until = elapsed + 0.5;
          shake.magnitude = 0.45;
          launchNuke(winner);
        }
      }
    };

    // ---- ambient firing (front-line soldiers periodically fire) -------
    const ambientFire = (dt) => {
      const doFire = (arr, isBull) => {
        for (const s of arr) {
          if (!s.alive || s.deathAt) continue;
          if (elapsed < s.nextFireAt) continue;
          // Only front-line soldiers fire
          const front = currentFrontLineX + frontLineWave(s.z);
          if (Math.abs(s.x - front) > 3.5) {
            s.nextFireAt = elapsed + 1 + Math.random() * 3;
            continue;
          }
          // Fire toward a random point on the enemy line
          const enemyOffset = isBull ? 1.5 : -1.5;
          const targetX = front + enemyOffset;
          const targetZ = s.z + (Math.random() - 0.5) * 2;
          spawnTracer(isBull, s.x, 0.75, s.z, targetX, 0.7, targetZ, false);
          s.nextFireAt = elapsed + 1.5 + Math.random() * 2.5;
        }
      };
      doFire(bulls, true);
      doFire(bears, false);
    };

    // ---- animate loop -------------------------------------------------
    let raf = 0;
    let running = true;
    const UP = new THREE.Vector3(0, 1, 0);
    const tmpDir = new THREE.Vector3();
    const tmpQuat = new THREE.Quaternion();
    let debugNukeFiredAt = null;

    const animate = () => {
      if (!running) return;
      raf = requestAnimationFrame(animate);
      const dt = Math.min(0.1, clock.getDelta());
      elapsed += dt;
      if (resizePending) { applyResize(); resizePending = false; }

      // Debug: fire a nuke 5s after mount if ?debug=nuke
      if (debug && !debugNukeFiredAt && elapsed > 5) {
        launchNuke(Math.random() > 0.5 ? "bull" : "bear");
        debugNukeFiredAt = elapsed;
      }

      // ---- front-line smoothing ----------------------------------------
      if (elapsed - lastFrontUpdate > 0.2) {
        updateFrontLineTarget();
        lastFrontUpdate = elapsed;
      }
      const k = 1 - Math.exp(-dt / 0.6);
      currentFrontLineX += (targetFrontLineX - currentFrontLineX) * k;
      terrainMat.uniforms.uFrontLineX.value = currentFrontLineX;

      // Scorch age tick
      scorchState.age += dt;
      terrainMat.uniforms.uScorchAge.value = scorchState.age;
      terrainMat.uniforms.uScorchX.value = scorchState.x;
      terrainMat.uniforms.uScorchZ.value = scorchState.z;

      // Sky brightening on nuke
      if (elapsed < skyFlashUntil) {
        const f = (skyFlashUntil - elapsed) / 3.0;
        scene.fog.color.setHex(0xffaa60).multiplyScalar(f).addScalar(0.05);
      } else {
        scene.fog.color.setHex(COLORS.fog);
      }

      // ---- rebuild trench barbed wire + haze along updated curve ------
      for (let i = 0; i <= 40; i++) {
        const z = -PLANE_H / 2 + (PLANE_H * i) / 40;
        wirePoints[i].set(currentFrontLineX + frontLineWave(z), 0.35, z);
      }
      const newWireGeo = new THREE.TubeGeometry(wireCurve, 40, 0.025, 4, false);
      wire.geometry.dispose(); wire.geometry = newWireGeo;
      const newHazeGeo = new THREE.TubeGeometry(wireCurve, 40, 0.35, 6, false);
      haze.geometry.dispose(); haze.geometry = newHazeGeo;
      hazeGeo = newHazeGeo;
      hazeMat.opacity = 0.15 + Math.sin(elapsed * 2) * 0.05;

      // ---- pulse warning lights on nukes ------------------------------
      const bullWarn = bullNuke.getObjectByName("warningLight");
      const bearWarn = bearNuke.getObjectByName("warningLight");
      if (bullWarn) {
        const inCooldown = nukeState.bull.phase === "reloading";
        const rate = inCooldown ? 3 : nukeState.bull.phase === "launching" ? 8 : 1.5;
        bullWarn.material.opacity = 0.6 + Math.sin(elapsed * Math.PI * 2 * rate) * 0.4;
        bullWarn.visible = nukeState.bull.phase !== "flight";
      }
      if (bearWarn) {
        const inCooldown = nukeState.bear.phase === "reloading";
        const rate = inCooldown ? 3 : nukeState.bear.phase === "launching" ? 8 : 1.5;
        bearWarn.material.opacity = 0.6 + Math.sin(elapsed * Math.PI * 2 * rate) * 0.4;
        bearWarn.visible = nukeState.bear.phase !== "flight";
      }

      // ---- drive nuke sequences ---------------------------------------
      const runNuke = (side) => {
        const st = nukeState[side];
        const nukeMesh = side === "bull" ? bullNuke : bearNuke;
        const startX = side === "bull" ? NUKE_BULL_X : NUKE_BEAR_X;
        const startZ = PLANE_H / 2 - 3.5;
        const targetX = side === "bull"
          ? NUKE_BEAR_X + (Math.random() - 0.5) * 3
          : NUKE_BULL_X + (Math.random() - 0.5) * 3;
        if (st.phase === "launching" || st.phase === "flight") {
          const t = (elapsed - st.launchStartedAt) / 2.2; // 0..1 flight
          if (t < 1) {
            const arcHeight = 10;
            const cx = startX + (targetX - startX) * t;
            const cy = 0.35 + Math.sin(t * Math.PI) * arcHeight;
            const cz = startZ + (st.targetZ - startZ) * t;
            nukeMesh.position.set(cx, cy, cz);
            const t2 = Math.min(1, t + 0.05);
            const nx = startX + (targetX - startX) * t2;
            const ny = 0.35 + Math.sin(t2 * Math.PI) * arcHeight;
            const nz = startZ + (st.targetZ - startZ) * t2;
            tmpDir.set(nx - cx, ny - cy, nz - cz).normalize();
            tmpQuat.setFromUnitVectors(UP, tmpDir);
            nukeMesh.quaternion.copy(tmpQuat);
            // Smoke trail
            if (Math.random() < 0.6) {
              dust.push({
                x: cx, y: cy, z: cz,
                driftX: (Math.random() - 0.5) * 0.3,
                driftY: 0.2 + Math.random() * 0.3,
                driftZ: (Math.random() - 0.5) * 0.3,
                spawnedAt: elapsed,
                colorHex: 0x555555,
              });
            }
            st.phase = t < 0.4 ? "launching" : "flight";
          } else {
            const ix = targetX;
            const iz = st.targetZ;
            const winner = side;
            const loser = winner === "bull" ? "bear" : "bull";
            const killed = killByBlast(ix, iz, BLAST_RADIUS, loser);
            for (let i = 0; i < killed && i < 12; i++) {
              if (onKillRef.current) {
                try { onKillRef.current(loser); } catch { /* ignore */ }
              }
            }
            mushroomStacks.push({ x: ix, z: iz, startedAt: elapsed });
            blastRings.push({ x: ix, z: iz, startedAt: elapsed });
            impactFlash.active = true;
            impactFlash.startedAt = elapsed;
            impactFlash.x = ix;
            impactFlash.z = iz;
            shake.until = elapsed + 0.8;
            shake.magnitude = 0.6;
            scorchState.x = ix; scorchState.z = iz; scorchState.age = 0;
            nukeMesh.visible = false;
            st.phase = "reloading";
            st.cooldownUntil = elapsed + NUKE_COOLDOWN;
          }
        } else if (st.phase === "reloading") {
          if (elapsed >= st.cooldownUntil) {
            st.phase = "idle";
            nukeMesh.position.set(startX, 0.35, startZ);
            nukeMesh.quaternion.identity();
            nukeMesh.visible = true;
          }
        }
      };
      runNuke("bull");
      runNuke("bear");
      publishNukeState();

      // ---- process trade events → combat -----------------------------
      drainHits();

      // ---- ambient firing --------------------------------------------
      ambientFire(dt);

      // ---- soldier updates -------------------------------------------
      const updateSoldier = (s) => {
        if (!s.alive) {
          if (elapsed >= s.respawnAt) {
            const isBull = s.species === "bull";
            s.x = isBull
              ? T_MIN_X + Math.random() * (T_MAX_X - T_MIN_X) * 0.55
              : T_MAX_X - Math.random() * (T_MAX_X - T_MIN_X) * 0.55;
            s.z = T_MIN_Z + Math.random() * (T_MAX_Z - T_MIN_Z);
            s.yaw = isBull ? 0 : Math.PI;
            s.role = Math.random() < 0.7 ? "advancer" : "support";
            s.deathAt = 0; s.respawnAt = 0; s.dustSpawned = false; s.alive = true;
            s.nextFireAt = elapsed + 1 + Math.random() * 3;
          }
          return;
        }
        if (s.deathAt) {
          const dd = elapsed - s.deathAt;
          if (dd >= 0.6) {
            s.alive = false;
            s.respawnAt = elapsed + 0.5;
            return;
          }
          if (dd >= 0.15 && !s.dustSpawned) {
            const bloodOrDust = s.species === "bull" ? COLORS.blood : COLORS.dust;
            spawnDust(s.x, s.z, bloodOrDust, 6);
            s.dustSpawned = true;
          }
          return;
        }
        // Movement
        const isBull = s.species === "bull";
        const front = currentFrontLineX + frontLineWave(s.z);
        if (s.role === "advancer") {
          const target = front + (isBull ? -0.8 : 0.8);
          const dx = target - s.x;
          const step = 0.28 * dt;
          if (Math.abs(dx) > step) s.x += Math.sign(dx) * step; else s.x = target;
          s.z += Math.sin(elapsed * 1.2 + s.wobbleSeed) * 0.006;
          s.yaw = isBull ? 0 : Math.PI;
        } else {
          if (elapsed > s.wanderChangeAt) {
            s.wanderDir = Math.random() * Math.PI * 2;
            s.wanderChangeAt = elapsed + 3 + Math.random() * 2;
          }
          s.x += Math.cos(s.wanderDir) * 0.12 * dt;
          s.z += Math.sin(s.wanderDir) * 0.12 * dt;
          const buffer = 2.5;
          if (isBull) {
            const maxX = front - buffer;
            if (s.x > maxX) s.x = maxX;
            if (s.x < T_MIN_X) s.x = T_MIN_X;
          } else {
            const minX = front + buffer;
            if (s.x < minX) s.x = minX;
            if (s.x > T_MAX_X) s.x = T_MAX_X;
          }
          if (s.z > T_MAX_Z) s.z = T_MAX_Z;
          if (s.z < T_MIN_Z) s.z = T_MIN_Z;
          s.yaw = Math.atan2(-Math.sin(s.wanderDir), Math.cos(s.wanderDir));
        }
      };
      for (const s of bulls) updateSoldier(s);
      for (const s of bears) updateSoldier(s);

      // ---- emit soldier matrices --------------------------------------
      const emitFor = (arr, gruntMesh, officerMesh) => {
        let g = 0, o = 0;
        for (const s of arr) {
          if (!s.alive) continue;
          let scale = 1;
          let rotZ = 0;
          if (s.deathAt) {
            const dd = elapsed - s.deathAt;
            if (dd < 0.15) {
              // Backward tilt as if hit
              rotZ = -dd * 3;
            } else {
              // Falling + fading via scale
              rotZ = -Math.PI / 3;
              scale = Math.max(0.001, 1 - (dd - 0.15) / 0.45);
            }
          }
          const bob = s.deathAt ? 0 : Math.sin(elapsed * 4 + s.wobbleSeed) * 0.04;
          dummy.position.set(s.x, bob, s.z);
          dummy.rotation.set(0, s.yaw, rotZ);
          dummy.scale.setScalar(scale * 1.05);
          dummy.updateMatrix();
          if (s.isOfficer) { officerMesh.setMatrixAt(o++, dummy.matrix); }
          else { gruntMesh.setMatrixAt(g++, dummy.matrix); }
        }
        gruntMesh.count = g;
        officerMesh.count = o;
        gruntMesh.instanceMatrix.needsUpdate = true;
        officerMesh.instanceMatrix.needsUpdate = true;
      };
      emitFor(bulls, bullGruntMesh, bullOfficerMesh);
      emitFor(bears, bearGruntMesh, bearOfficerMesh);

      // ---- update tracers ---------------------------------------------
      let bullSlot = 0, bearSlot = 0;
      for (let i = tracers.length - 1; i >= 0; i--) {
        const p = tracers[i];
        const t = (elapsed - p.spawnedAt) / p.duration;
        if (t >= 1) { tracers.splice(i, 1); continue; }
        const lerpX = p.fromX + (p.toX - p.fromX) * t;
        const lerpY = p.fromY + (p.toY - p.fromY) * t + Math.sin(t * Math.PI) * 0.15;
        const lerpZ = p.fromZ + (p.toZ - p.fromZ) * t;
        const dx = p.toX - p.fromX;
        const dy = p.toY - p.fromY;
        const dz = p.toZ - p.fromZ;
        tmpDir.set(dx, dy, dz).normalize();
        tmpQuat.setFromUnitVectors(UP, tmpDir);
        dummy.position.set(lerpX, lerpY, lerpZ);
        dummy.quaternion.copy(tmpQuat);
        dummy.scale.set(1, 1, 1);
        dummy.updateMatrix();
        if (p.isBullShot) {
          if (bullSlot < MAX_TRACERS) { bullTracerMesh.setMatrixAt(bullSlot, dummy.matrix); bullSlot++; }
        } else {
          if (bearSlot < MAX_TRACERS) { bearTracerMesh.setMatrixAt(bearSlot, dummy.matrix); bearSlot++; }
        }
      }
      bullTracerMesh.count = bullSlot;
      bearTracerMesh.count = bearSlot;
      bullTracerMesh.instanceMatrix.needsUpdate = true;
      bearTracerMesh.instanceMatrix.needsUpdate = true;

      // ---- muzzle flashes ---------------------------------------------
      let fSlot = 0;
      for (let i = flashes.length - 1; i >= 0; i--) {
        const f = flashes[i];
        const t = (elapsed - f.spawnedAt) / f.duration;
        if (t >= 1) { flashes.splice(i, 1); continue; }
        const s = (0.5 + t * 1.5) * f.size;
        dummy.position.set(f.x, f.y, f.z);
        dummy.rotation.set(0, 0, 0);
        dummy.scale.setScalar(s);
        dummy.updateMatrix();
        if (fSlot < MAX_FLASHES) { flashMesh.setMatrixAt(fSlot, dummy.matrix); fSlot++; }
      }
      flashMesh.count = fSlot;
      flashMesh.instanceMatrix.needsUpdate = true;

      // ---- dust puffs -------------------------------------------------
      let dSlot = 0;
      const DUST_DUR = 0.6;
      for (let i = dust.length - 1; i >= 0; i--) {
        const d = dust[i];
        const t = (elapsed - d.spawnedAt) / DUST_DUR;
        if (t >= 1) { dust.splice(i, 1); continue; }
        const px = d.x + d.driftX * t;
        const py = d.y + d.driftY * t * (1 - t * 0.8);
        const pz = d.z + d.driftZ * t;
        const scale = 0.7 + t * 1.4;
        dummy.position.set(px, py, pz);
        dummy.rotation.set(0, 0, 0);
        dummy.scale.setScalar(scale);
        dummy.updateMatrix();
        if (dSlot < MAX_DUST) { dustMesh.setMatrixAt(dSlot, dummy.matrix); dSlot++; }
      }
      dustMesh.count = dSlot;
      dustMesh.instanceMatrix.needsUpdate = true;

      // ---- mushroom clouds --------------------------------------------
      // Rebuild all cloud meshes each frame. They are cheap (few layers)
      // but for large impacts we keep at most 3 active.
      // Remove finished clouds
      for (let i = mushroomStacks.length - 1; i >= 0; i--) {
        const c = mushroomStacks[i];
        const t = (elapsed - c.startedAt) / 5.0; // full lifetime 5s
        if (t >= 1) {
          if (c.group) scene.remove(c.group);
          mushroomStacks.splice(i, 1);
          continue;
        }
        // Build or update group
        if (!c.group) {
          const g = new THREE.Group();
          g.position.set(c.x, 0, c.z);
          c.layers = [];
          for (let j = 0; j < 5; j++) {
            const jColor = j < 2 ? COLORS.mushroomBase : j < 4 ? COLORS.mushroomMid : COLORS.mushroomTop;
            const mat = new THREE.MeshBasicMaterial({
              color: jColor, toneMapped: false, transparent: true, opacity: 0.9,
            });
            const m = new THREE.Mesh(layerGeo.clone(), mat);
            m.userData.baseY = j * 1.8;
            m.userData.baseR = j === 0 ? 2.8 : j === 4 ? 4.5 : 3.0 + j * 0.4;
            g.add(m);
            c.layers.push(m);
          }
          scene.add(g);
          c.group = g;
        }
        // Animate: grow 0→1 in first 30% of lifetime, then rise + fade
        const grow = Math.min(1, t / 0.3);
        const rise = Math.max(0, (t - 0.3) / 0.7);
        for (let j = 0; j < c.layers.length; j++) {
          const m = c.layers[j];
          const r = m.userData.baseR * grow;
          m.scale.setScalar(r);
          m.position.y = m.userData.baseY * grow + rise * 3;
          m.material.opacity = 0.9 * (1 - rise * 0.7);
        }
      }

      // ---- blast rings ------------------------------------------------
      for (let i = blastRings.length - 1; i >= 0; i--) {
        const r = blastRings[i];
        const t = (elapsed - r.startedAt) / 0.5;
        if (t >= 1) {
          if (r.mesh) scene.remove(r.mesh);
          blastRings.splice(i, 1);
          continue;
        }
        if (!r.mesh) {
          const m = new THREE.Mesh(ringGeo, ringMat.clone());
          m.position.set(r.x, 0.05, r.z);
          scene.add(m);
          r.mesh = m;
        }
        const size = 1 + t * BLAST_RADIUS;
        r.mesh.scale.set(size, 1, size);
        r.mesh.material.opacity = 0.8 * (1 - t);
      }

      // ---- impact flash sphere -----------------------------------------
      if (impactFlash.active) {
        const t = (elapsed - impactFlash.startedAt) / 0.15;
        if (t >= 1) {
          impactFlash.active = false;
          flashSphere.visible = false;
        } else {
          flashSphere.visible = true;
          flashSphere.position.set(impactFlash.x, 3, impactFlash.z);
          flashSphere.scale.setScalar(1 + t * 3);
          flashSphere.material.opacity = 0.9 * (1 - t);
        }
      }

      // ---- radar dish rotation ----------------------------------------
      bullRadar.rotation.y = elapsed * 0.5;
      bearRadar.rotation.y = -elapsed * 0.5;

      // ---- camera -----------------------------------------------------
      let camAngle;
      if (compact) camAngle = 0;
      else if (autoOrbitRef.current) camAngle = (elapsed / 45) * Math.PI * 2;
      else camAngle = Math.sin(elapsed / 8) * (Math.PI / 180) * 0.6;
      let dist = camDistBase;
      camera.position.set(Math.sin(camAngle) * dist, camY, Math.cos(camAngle) * dist);
      if (elapsed < shake.until) {
        const p = (shake.until - elapsed) / 0.8;
        camera.position.x += (Math.random() - 0.5) * shake.magnitude * p * 2;
        camera.position.y += (Math.random() - 0.5) * shake.magnitude * p * 2;
      }
      camera.lookAt(0, 0, 0);

      // ---- render -----------------------------------------------------
      if (hqRef.current) initComposer().render();
      else renderer.render(scene, camera);
    };
    animate();

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      scene.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
          mats.forEach((m) => { if (m.map) m.map.dispose(); m.dispose(); });
        }
      });
      if (composer) composer.dispose();
      renderer.dispose();
    };
  }, [contextStatus, mobile, compact, debug, bookRef, hitsRef, canvasRef, nukeStateRef]);

  if (contextStatus === "fallback-2d") {
    return (
      <>
        <BattleCanvas ref={ref} bookRef={bookRef} hitsRef={hitsRef} mobile={mobile} topClass={topClass} heightClass={heightClass} />
        <span className="sr-only" data-testid="webgl-context-status" data-context="fallback-2d" aria-hidden="true" />
      </>
    );
  }

  return (
    <>
      <canvas
        ref={canvasRef}
        className={`absolute ${topClass} left-0 w-full ${heightClass} z-10 block`}
        data-testid="battlefield-canvas"
        aria-label="Live order book rendered as a modern-military 3D battlefield"
      />
      <span
        className="sr-only"
        data-testid="webgl-context-status"
        data-context={contextStatus || "initializing"}
        aria-hidden="true"
      />
    </>
  );
});

export default Battlefield3D;
